import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  queryCollection, 
  createDocument,
  updateDocument,
  uploadFile,
  generateOrganizationSubdomainCode,
  Organization 
} from '../lib/api';
import { invalidateCache } from '../lib/portalCache';
import { 
  PortalSettings, 
  TemplateId, 
  SectionId,
  PortalColors,
  TEMPLATE_CONFIGS, 
  SECTION_CONFIGS 
} from '../types/portal';
import { 
  Globe, 
  Palette, 
  Image,
  Eye,
  Check,
  CaretLeft,
  Desktop,
  DeviceMobile,
  Newspaper,
  FileText,
  Phone,
  Warning,
  Calendar,
  Images,
  Link,
  Copy,
  Trash,
  ArrowClockwise,
  Info,
  MapPin
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Base domain for subdomains
const BASE_DOMAIN = 'grindstugatan.se';

// Section icon mapping
const SECTION_ICONS: Record<SectionId, React.ComponentType<{ size?: number; className?: string; color?: string }>> = {
  news: Newspaper,
  documents: FileText,
  contact: Phone,
  issues: Warning,
  calendar: Calendar,
  gallery: Images
};

// Template preview images (placeholders for now)
const TEMPLATE_PREVIEWS: Record<TemplateId, string> = {
  modern: '/api/placeholder/400/250',
  classic: '/api/placeholder/400/250',
  minimalist: '/api/placeholder/400/250'
};

export default function PortalAdminPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [copied, setCopied] = useState(false);
  const [copiedSubdomain, setCopiedSubdomain] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  
  // Portal settings state
  const [settings, setSettings] = useState<PortalSettings>({
    organization_id: '',
    template_id: 'modern',
    colors: TEMPLATE_CONFIGS[0].default_colors,
    visible_sections: ['news', 'documents', 'contact'],
    meta_title: '',
    meta_description: '',
    footer_text: ''
  });

  useEffect(() => {
    loadData();
  }, [user?.organization_id]);

  const loadData = async () => {
    if (!user?.organization_id) return;
    
    setLoading(true);
    try {
      // Load organization
      const orgs = await queryCollection<Organization>('organizations', { id: user.organization_id });
      if (orgs.length > 0) {
        const org = orgs[0];
        setOrganization(org);
        
        // Auto-generate subdomain code if not exists
        if (!org.subdomain_code && org.id) {
          const code = await generateOrganizationSubdomainCode(org.id);
          if (code) {
            setOrganization({ ...org, subdomain_code: code });
          }
        }
        
        // Check for existing portal settings
        const existingSettings = await queryCollection<PortalSettings>('portal_settings', { 
          organization_id: user.organization_id 
        });
        
        if (existingSettings.length > 0) {
          setSettings(existingSettings[0]);
        } else {
          // Initialize with defaults
          setSettings(prev => ({
            ...prev,
            organization_id: user.organization_id!,
            meta_title: org.name || '',
            meta_description: `Välkommen till ${org.name || 'vår förening'}`
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Kunde inte ladda inställningar');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: TemplateId) => {
    const templateConfig = TEMPLATE_CONFIGS.find(t => t.id === templateId);
    if (templateConfig) {
      setSettings(prev => ({
        ...prev,
        template_id: templateId,
        colors: templateConfig.default_colors
      }));
    }
  };

  const handleColorChange = (key: keyof PortalColors, value: string) => {
    setSettings(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [key]: value
      }
    }));
  };

  const handleSectionToggle = (sectionId: SectionId) => {
    setSettings(prev => {
      const isVisible = prev.visible_sections.includes(sectionId);
      return {
        ...prev,
        visible_sections: isVisible
          ? prev.visible_sections.filter(s => s !== sectionId)
          : [...prev.visible_sections, sectionId]
      };
    });
  };

  const handleImageUpload = async (file: File, type: 'logo' | 'hero') => {
    try {
      const result = await uploadFile(file);
      const imageUrl = result.url || result.file_url || '';
      
      if (type === 'logo') {
        setSettings(prev => ({ ...prev, logo_url: imageUrl }));
      } else {
        setSettings(prev => ({ ...prev, hero_image_url: imageUrl }));
      }
      toast.success('Bild uppladdad');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Kunde inte ladda upp bild');
    }
  };

  const handleSave = async () => {
    if (!user?.organization_id) return;
    
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const settingsData = {
        ...settings,
        organization_id: user.organization_id,
        updated_at: now
      };

      if (settings.id) {
        // Update existing
        await updateDocument('portal_settings', settings.id, settingsData);
      } else {
        // Create new
        const result = await createDocument('portal_settings', {
          ...settingsData,
          created_at: now
        });
        setSettings(prev => ({ ...prev, id: result.id }));
      }
      
      // Invalidate portal cache so visitors see updated settings
      if (organization?.slug) {
        invalidateCache(organization.slug);
      }
      
      toast.success('Portalinställningar sparade!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Kunde inte spara inställningar');
    } finally {
      setSaving(false);
    }
  };

  const copyPortalUrl = () => {
    const slug = organization?.slug || '';
    const url = `${window.location.origin}/portal/${slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Länk kopierad!');
  };

  const copySubdomainUrl = () => {
    const code = organization?.subdomain_code || '';
    const url = `https://${code}.${BASE_DOMAIN}`;
    navigator.clipboard.writeText(url);
    setCopiedSubdomain(true);
    setTimeout(() => setCopiedSubdomain(false), 2000);
    toast.success('Subdomän-länk kopierad!');
  };

  const handleRegenerateCode = async () => {
    if (!organization?.id) return;
    
    setGeneratingCode(true);
    try {
      const newCode = await generateOrganizationSubdomainCode(organization.id);
      if (newCode) {
        setOrganization({ ...organization, subdomain_code: newCode });
        toast.success('Ny subdomän-kod genererad!');
      } else {
        toast.error('Kunde inte generera ny kod');
      }
    } catch (error) {
      toast.error('Något gick fel');
    } finally {
      setGeneratingCode(false);
    }
  };

  const portalUrl = organization?.slug 
    ? `${window.location.origin}/portal/${organization.slug}`
    : '';

  const subdomainUrl = organization?.subdomain_code
    ? `https://${organization.subdomain_code}.${BASE_DOMAIN}`
    : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-primary rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="portal-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Webbportal</h1>
          <p className="text-slate-500 mt-1">Anpassa er publika medlemsportal</p>
        </div>
        <div className="flex gap-2">
          {portalUrl && (
            <a href={portalUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="gap-2">
                <Eye size={16} />
                Förhandsgranska
              </Button>
            </a>
          )}
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                Sparar...
              </>
            ) : (
              <>
                <Check size={16} />
                Spara ändringar
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Portal URL */}
      {portalUrl && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Link size={20} className="text-slate-400" />
              <div className="flex-1">
                <p className="text-sm text-slate-500">Portal-adress (intern)</p>
                <p className="font-medium text-slate-900">{portalUrl}</p>
              </div>
              <Button variant="outline" size="sm" onClick={copyPortalUrl} className="gap-2">
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Kopierad!' : 'Kopiera'}
              </Button>
            </div>
            {settings.custom_domain && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <p className="text-sm text-slate-500">Egen domän: <span className="font-medium text-slate-700">{settings.custom_domain}</span></p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subdomain Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe size={20} className="text-primary" />
            Er unika portal-adress
          </CardTitle>
          <CardDescription>
            Varje BRF får en unik subdomän som ni kan använda direkt eller peka er egen domän till
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Subdomain URL */}
          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
            <div className="flex-1">
              <p className="text-xs text-slate-500 mb-1">Subdomän</p>
              <p className="font-mono font-medium text-slate-900">
                {organization?.subdomain_code ? (
                  <>{organization.subdomain_code}.{BASE_DOMAIN}</>
                ) : (
                  <span className="text-slate-400">Genererar...</span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={copySubdomainUrl}
                disabled={!organization?.subdomain_code}
                className="gap-2"
              >
                {copiedSubdomain ? <Check size={14} /> : <Copy size={14} />}
                {copiedSubdomain ? 'Kopierad!' : 'Kopiera'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRegenerateCode}
                disabled={generatingCode}
                title="Generera ny kod"
              >
                <ArrowClockwise size={14} className={generatingCode ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>

          {/* DNS Instructions */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Info size={18} className="text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Koppla egen domän</p>
                <p className="text-xs text-slate-600 mt-1">
                  Vill ni använda en egen adress (t.ex. <span className="font-mono">medlem.ertbrf.se</span>)? 
                  Skapa en CNAME-post hos er DNS-leverantör:
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded border border-slate-200 p-3 font-mono text-xs">
              <div className="grid grid-cols-3 gap-2 text-slate-500 mb-2">
                <span>Typ</span>
                <span>Namn</span>
                <span>Värde</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-slate-900">
                <span>CNAME</span>
                <span>medlem</span>
                <span className="break-all">{organization?.subdomain_code}.{BASE_DOMAIN}</span>
              </div>
            </div>
            
            <p className="text-xs text-slate-500">
              Efter att DNS-posten är skapad och propagerat (kan ta upp till 24h), fyll i er domän nedan under &quot;Egen domän&quot;.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Template Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe size={20} className="text-primary" />
                Välj mall
              </CardTitle>
              <CardDescription>
                Välj en grunddesign för er portal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {TEMPLATE_CONFIGS.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateChange(template.id)}
                    className={cn(
                      "relative p-4 rounded-xl border-2 transition-all text-left",
                      settings.template_id === template.id
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    )}
                    data-testid={`template-${template.id}`}
                  >
                    {/* Template preview */}
                    <div 
                      className="h-24 rounded-lg mb-3 flex items-center justify-center"
                      style={{ 
                        background: `linear-gradient(135deg, ${template.default_colors.primary}, ${template.default_colors.secondary})`
                      }}
                    >
                      <span className="text-white/80 text-xs font-medium">{template.name}</span>
                    </div>
                    
                    <h3 className="font-semibold text-slate-900">{template.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{template.description}</p>
                    
                    {settings.template_id === template.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette size={20} className="text-primary" />
                Färger
              </CardTitle>
              <CardDescription>
                Anpassa portalens färgschema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { key: 'primary' as const, label: 'Primär', description: 'Huvudfärg för knappar och länkar' },
                  { key: 'secondary' as const, label: 'Sekundär', description: 'Kompletterande färg' },
                  { key: 'accent' as const, label: 'Accent', description: 'Accentfärg för detaljer' },
                  { key: 'background' as const, label: 'Bakgrund', description: 'Sidans bakgrundsfärg' },
                  { key: 'text' as const, label: 'Text', description: 'Huvudfärg för text' }
                ].map((colorConfig) => (
                  <div key={colorConfig.key} className="space-y-2">
                    <Label className="text-sm font-medium">{colorConfig.label}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.colors[colorConfig.key] || '#000000'}
                        onChange={(e) => handleColorChange(colorConfig.key, e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                        data-testid={`color-${colorConfig.key}`}
                      />
                      <Input
                        value={settings.colors[colorConfig.key] || ''}
                        onChange={(e) => handleColorChange(colorConfig.key, e.target.value)}
                        placeholder="#000000"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-slate-400">{colorConfig.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Sections */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={20} className="text-primary" />
                Sektioner
              </CardTitle>
              <CardDescription>
                Välj vilka sektioner som ska visas på portalen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SECTION_CONFIGS.map((section) => {
                  const Icon = SECTION_ICONS[section.id];
                  const isVisible = settings.visible_sections.includes(section.id);
                  
                  return (
                    <label
                      key={section.id}
                      className={cn(
                        "flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all",
                        isVisible 
                          ? "border-primary/30 bg-primary/5" 
                          : "border-slate-200 hover:border-slate-300"
                      )}
                      data-testid={`section-${section.id}`}
                    >
                      <Checkbox
                        checked={isVisible}
                        onCheckedChange={() => handleSectionToggle(section.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon size={16} className={isVisible ? "text-primary" : "text-slate-400"} />
                          <span className="font-medium text-slate-900">{section.name}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{section.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Images */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image size={20} className="text-primary" />
                Bilder
              </CardTitle>
              <CardDescription>
                Ladda upp logotyp och omslagsbild
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo */}
              <div>
                <Label className="text-sm font-medium">Logotyp</Label>
                <div className="flex items-start gap-4 mt-2">
                  {settings.logo_url ? (
                    <div className="relative">
                      <img 
                        src={settings.logo_url} 
                        alt="Logo" 
                        className="w-20 h-20 rounded-lg object-contain bg-slate-100 p-2"
                      />
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, logo_url: undefined }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Image size={24} className="text-slate-300" />
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file, 'logo');
                      }}
                      data-testid="upload-logo"
                    />
                    <p className="text-xs text-slate-500 mt-1">Rekommenderad storlek: 200×200px</p>
                  </div>
                </div>
              </div>

              {/* Hero image */}
              <div>
                <Label className="text-sm font-medium">Omslagsbild</Label>
                <div className="mt-2">
                  {settings.hero_image_url ? (
                    <div className="relative">
                      <img 
                        src={settings.hero_image_url} 
                        alt="Hero" 
                        className="w-full h-40 rounded-lg object-cover"
                      />
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, hero_image_url: undefined }))}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-full h-40 rounded-lg bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-200">
                      <div className="text-center">
                        <Image size={32} className="text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">Ingen bild vald</p>
                      </div>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, 'hero');
                    }}
                    className="mt-2"
                    data-testid="upload-hero"
                  />
                  <p className="text-xs text-slate-500 mt-1">Rekommenderad storlek: 1920×600px</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SEO & Custom Domain */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe size={20} className="text-primary" />
                SEO & Domän
              </CardTitle>
              <CardDescription>
                Inställningar för sökmotorer och egen domän
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Sidtitel (meta title)</Label>
                <Input
                  value={settings.meta_title || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, meta_title: e.target.value }))}
                  placeholder={organization?.name || 'Föreningens namn'}
                  className="mt-1"
                  data-testid="meta-title"
                />
              </div>
              
              <div>
                <Label>Beskrivning (meta description)</Label>
                <Textarea
                  value={settings.meta_description || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, meta_description: e.target.value }))}
                  placeholder="Kort beskrivning av föreningen..."
                  className="mt-1"
                  rows={2}
                  data-testid="meta-description"
                />
              </div>
              
              <div>
                <Label>Egen domän (valfritt)</Label>
                <Input
                  value={settings.custom_domain || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, custom_domain: e.target.value }))}
                  placeholder="medlem.example.se"
                  className="mt-1"
                  data-testid="custom-domain"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Kontakta oss för att koppla en egen domän till portalen
                </p>
              </div>
              
              <div>
                <Label>Sidfot-text</Label>
                <Input
                  value={settings.footer_text || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, footer_text: e.target.value }))}
                  placeholder={`© ${new Date().getFullYear()} ${organization?.name || 'Föreningen'}`}
                  className="mt-1"
                  data-testid="footer-text"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Visibility Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone size={20} />
                Kontaktuppgifter i portalen
              </CardTitle>
              <CardDescription>
                Välj vilka kontaktuppgifter som ska visas på den publika portalen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded">
                    <Phone size={18} />
                  </div>
                  <div>
                    <p className="font-medium">Telefonnummer</p>
                    <p className="text-sm text-slate-500">{organization?.contact_phone || 'Ej angivet'}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!settings.hide_phone}
                    onChange={(e) => setSettings(prev => ({ ...prev, hide_phone: !e.target.checked }))}
                    className="sr-only peer"
                    data-testid="toggle-phone"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ml-2 text-sm text-slate-600">{settings.hide_phone ? 'Dold' : 'Synlig'}</span>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded">
                    <Globe size={18} />
                  </div>
                  <div>
                    <p className="font-medium">E-postadress</p>
                    <p className="text-sm text-slate-500">{organization?.contact_email || 'Ej angivet'}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!settings.hide_email}
                    onChange={(e) => setSettings(prev => ({ ...prev, hide_email: !e.target.checked }))}
                    className="sr-only peer"
                    data-testid="toggle-email"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ml-2 text-sm text-slate-600">{settings.hide_email ? 'Dold' : 'Synlig'}</span>
                </label>
              </div>

              {/* Address */}
              <div className="flex items-start justify-between pt-2 border-t border-slate-100">
                <div className="flex items-start gap-3 flex-1 mr-4">
                  <div className="p-2 bg-slate-100 rounded mt-1">
                    <MapPin size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium mb-2">Adress</p>
                    <Input
                      value={settings.contact_address || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, contact_address: e.target.value }))}
                      placeholder="T.ex. Grindstugatan 1, 123 45 Stockholm"
                      className="text-sm"
                      data-testid="contact-address"
                    />
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer mt-1">
                  <input
                    type="checkbox"
                    checked={!settings.hide_address}
                    onChange={(e) => setSettings(prev => ({ ...prev, hide_address: !e.target.checked }))}
                    className="sr-only peer"
                    data-testid="toggle-address"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ml-2 text-sm text-slate-600">{settings.hide_address ? 'Dold' : 'Synlig'}</span>
                </label>
              </div>

              <p className="text-xs text-slate-400 mt-2">
                Tips: Telefon och e-post hämtas från organisationsinställningarna. Adressen kan du ange här.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Right column - Live Preview */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Förhandsgranskning</CardTitle>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={cn(
                        "p-2 rounded transition-colors",
                        previewMode === 'desktop' ? "bg-slate-100" : "hover:bg-slate-50"
                      )}
                    >
                      <Desktop size={16} className={previewMode === 'desktop' ? "text-primary" : "text-slate-400"} />
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={cn(
                        "p-2 rounded transition-colors",
                        previewMode === 'mobile' ? "bg-slate-100" : "hover:bg-slate-50"
                      )}
                    >
                      <DeviceMobile size={16} className={previewMode === 'mobile' ? "text-primary" : "text-slate-400"} />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Mini preview */}
                <div 
                  className={cn(
                    "rounded-lg overflow-hidden border shadow-sm transition-all mx-auto",
                    previewMode === 'mobile' ? "w-40" : "w-full"
                  )}
                  style={{ backgroundColor: settings.colors.background || '#ffffff' }}
                >
                  {/* Hero */}
                  <div 
                    className="h-16 flex items-end p-2"
                    style={{ 
                      background: settings.hero_image_url 
                        ? `url(${settings.hero_image_url}) center/cover` 
                        : `linear-gradient(135deg, ${settings.colors.primary}, ${settings.colors.secondary})`
                    }}
                  >
                    {settings.logo_url && (
                      <img 
                        src={settings.logo_url} 
                        alt="Logo" 
                        className="w-8 h-8 rounded bg-white/90 p-1 object-contain"
                      />
                    )}
                  </div>
                  
                  {/* Content preview */}
                  <div className="p-2 space-y-2">
                    <div 
                      className="h-2 rounded w-2/3"
                      style={{ backgroundColor: settings.colors.text || '#1e293b' }}
                    />
                    <div 
                      className="h-1.5 rounded w-full opacity-40"
                      style={{ backgroundColor: settings.colors.text || '#1e293b' }}
                    />
                    <div 
                      className="h-1.5 rounded w-4/5 opacity-40"
                      style={{ backgroundColor: settings.colors.text || '#1e293b' }}
                    />
                    
                    {/* Section indicators */}
                    <div className="pt-2 flex flex-wrap gap-1">
                      {settings.visible_sections.slice(0, 4).map(sectionId => {
                        const IconComponent = SECTION_ICONS[sectionId];
                        return (
                          <div 
                            key={sectionId}
                            className="w-5 h-5 rounded flex items-center justify-center"
                            style={{ backgroundColor: settings.colors.primary, opacity: 0.2 }}
                          >
                            <IconComponent size={10} color={settings.colors.primary} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div 
                    className="h-4 px-2 flex items-center"
                    style={{ backgroundColor: settings.colors.primary, opacity: 0.1 }}
                  >
                    <div 
                      className="h-1 rounded w-1/2 opacity-50"
                      style={{ backgroundColor: settings.colors.text }}
                    />
                  </div>
                </div>
                
                <p className="text-xs text-slate-400 text-center mt-3">
                  {previewMode === 'desktop' ? 'Desktop-vy' : 'Mobil-vy'}
                </p>
              </CardContent>
            </Card>

            {/* Quick actions */}
            <Card className="mt-4">
              <CardContent className="py-4">
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      const template = TEMPLATE_CONFIGS.find(t => t.id === settings.template_id);
                      if (template) {
                        setSettings(prev => ({ ...prev, colors: template.default_colors }));
                        toast.success('Färger återställda till standardvärden');
                      }
                    }}
                  >
                    <Palette size={16} />
                    Återställ färger
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setSettings(prev => ({ 
                        ...prev, 
                        visible_sections: ['news', 'documents', 'contact', 'issues', 'calendar', 'gallery']
                      }));
                      toast.success('Alla sektioner aktiverade');
                    }}
                  >
                    <Check size={16} />
                    Aktivera alla sektioner
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
